import Phaser from 'phaser';
import { GameScene } from '../GameScene';
import { ACTION_KEY_IDS, type ActionKeyId } from '../../input/InputManager';
import { SITE_URL } from '@shared/constants';
import {
  SoundCategory,
  SOUND_CATEGORY_ORDER,
  SOUND_CATEGORY_LABELS_ES,
} from '@shared/data/sounds';
import { attachUiSound } from '../../audio/uiSound';
import { createUiButton, UI_BUTTON_PALETTES } from '../../ui/uiButton';

/** Controles de la sección "Sonido" (creados dinámicamente). */
export interface SoundControls {
  setVisible: (visible: boolean) => void;
  refresh: () => void;
  objects: Phaser.GameObjects.GameObject[];
}

/**
 * Menú principal in-game (botón ⚙ / Esc) + panel de Opciones: arma la barra de botones del HUD
 * (createMenuUI), abre/cierra el menú y construye el panel de Opciones con todos sus toggles
 * (grid debug, visibilidad de nombres, flechas de heading, paneles dev, logout…). Extraído de
 * GameScene (god-object). El estado del menú vive en GameScene (host, lo consultan input/cámara) y
 * se accede vía `this.scene.*`. Es UI event-driven (no per-frame).
 */
export class MenuController {
  constructor(private scene: GameScene) {}

  public createMenuUI(): void {
    const { width, height } = this.scene.scale;

    // =====================================================================
    // FILA 1: Botones principales (Personaje, Inventario, Árbol de Pasivas, Config)
    // =====================================================================
    const row1Y = 10;
    const row2Y = 40;
    const buttonGap = 40;

    // Botón de Config (tuerca) - extremo derecho
    this.scene.gearButton = this.scene.addText(width - 20, row1Y, '⚙', {
        fontSize: '20px',
        color: '#ffffff',
      })
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true })
      .setDepth(1000)
      .setScrollFactor(0);

    this.scene.gearButton.on('pointerup', () => {
      this.openMainMenu();
    });

    // Botón de sol (☀️): muestra/oculta el panel debug de hora del día (arranca oculto).
    // Vive en la FILA 2 (admin/debug), en el slot libre n=9 (más a la izquierda que el resto).
    this.scene.sunButton = this.scene.addText(width - 20 - buttonGap * 9, row2Y, '☀️', {
        fontSize: '20px',
        color: '#ffffff',
      })
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true })
      .setDepth(1000)
      .setScrollFactor(0);

    this.scene.sunButton.on('pointerup', () => {
      this.scene.lightingDebug.toggleLightingDebugPanel();
    });

    // Los botones de Personaje, Inventario y Árbol de Pasivas ahora están en el HUD inferior

    // =====================================================================
    // FILA 2: Botones de debug/desarrollo (Invoke, Items, Body Gallery, Anim)
    // =====================================================================
    // Botón de listado de invocación (/invocar)
    this.scene.invokeButton = this.scene.addText(width - 20, row2Y, '📜', {
        fontSize: '20px',
        color: '#ffffff',
      })
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true })
      .setDepth(1000)
      .setScrollFactor(0);

    this.scene.invokeButton.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      this.scene.devPanels.toggleInvokePanel();
      pointer.event.stopPropagation();
    });

    // Botón de listado de templates de items
    this.scene.itemsButton = this.scene.addText(width - 20 - buttonGap, row2Y, '💎', {
        fontSize: '20px',
        color: '#ffffff',
      })
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true })
      .setDepth(1000)
      .setScrollFactor(0);

    this.scene.itemsButton.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      this.scene.itemPanelCtl.toggleItemPanel();
      pointer.event.stopPropagation();
    });

    // Botón de galería de bodies
    this.scene.bodyGalleryButton = this.scene.addText(width - 20 - buttonGap * 2, row2Y, '🧍', {
        fontSize: '20px',
        color: '#ffffff',
      })
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true })
      .setDepth(1000)
      .setScrollFactor(0);

    this.scene.bodyGalleryButton.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      this.scene.devPanels.toggleBodyGalleryPanel();
      pointer.event.stopPropagation();
    });

    // Botón de galería de weapons (preview de anims de armas)
    this.scene.weaponGalleryButton = this.scene.addText(width - 20 - buttonGap * 5, row2Y, '⚔️', {
        fontSize: '20px',
        color: '#ffffff',
      })
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true })
      .setDepth(1000)
      .setScrollFactor(0);

    this.scene.weaponGalleryButton.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      this.scene.devPanels.toggleWeaponGalleryPanel();
      pointer.event.stopPropagation();
    });

    // Botón de debug de frames del personaje (stepper de cuadros)
    this.scene.charFrameButton = this.scene.addText(width - 20 - buttonGap * 6, row2Y, '🎞️', {
        fontSize: '20px',
        color: '#ffffff',
      })
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true })
      .setDepth(1000)
      .setScrollFactor(0);

    this.scene.charFrameButton.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      this.scene.devPanels.toggleCharFrameDebugPanel();
      pointer.event.stopPropagation();
    });

    // Botón de animaciones
    this.scene.animButton = this.scene.add
      .text(width - 20 - buttonGap * 3, row2Y, '✨', {
        fontSize: '20px',
        color: '#ffffff',
      })
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true })
      .setDepth(1000)
      .setScrollFactor(0);

    this.scene.animButton.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      this.scene.animPanelCtl.toggleAnimPanel();
      pointer.event.stopPropagation();
    });

    // Botón de partículas (lista FX del catálogo, invoca sobre local player)
    this.scene.particleButton = this.scene.add
      .text(width - 20 - buttonGap * 4, row2Y, '🎆', {
        fontSize: '20px',
        color: '#ffffff',
      })
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true })
      .setDepth(1000)
      .setScrollFactor(0);

    this.scene.particleButton.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      this.scene.particleFx.toggleParticlePanel();
      pointer.event.stopPropagation();
    });

    // Botón de debug: dar XP de habilidad (sólo admin lo aplica server-side).
    const debugXpButton = this.scene.add
      .text(width - 20 - buttonGap * 7, row2Y, '🎓', { fontSize: '20px', color: '#ffffff' })
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true })
      .setDepth(1000)
      .setScrollFactor(0);
    debugXpButton.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      this.scene.toggleDebugSkillXpPanel();
      pointer.event.stopPropagation();
    });

    // Botón del visor de sonidos (dev/admin).
    const soundBrowserButton = this.scene.add
      .text(width - 20 - buttonGap * 8, row2Y, '🔊', { fontSize: '20px', color: '#ffffff' })
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true })
      .setDepth(1000)
      .setScrollFactor(0);
    attachUiSound(soundBrowserButton);
    soundBrowserButton.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      this.scene.soundBrowser.toggle();
      pointer.event.stopPropagation();
    });

    // Botón del visor de gráficos indexeados (dev): grilla de texturas de personaje/ítem + detalle.
    const textureBrowserButton = this.scene.add
      .text(width - 20 - buttonGap * 10, row2Y, '🖼️', { fontSize: '20px', color: '#ffffff' })
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true })
      .setDepth(1000)
      .setScrollFactor(0);
    attachUiSound(textureBrowserButton);
    textureBrowserButton.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      this.scene.textureBrowser.toggle();
      pointer.event.stopPropagation();
    });

    // Overlay negro (depth mayor que el HUD para cubrirlo, menor que los botones del menú)

    this.scene.menuOverlay = this.scene.add

      .rectangle(0, 0, width, height, 0x000000, 0.8)

      .setOrigin(0, 0)

      .setDepth(1050)

      .setVisible(false)

      .setActive(false)

      .setScrollFactor(0);

    // Overlay solo visual: no debe capturar eventos de input.

    // Botones principales del menú

    const centerX = width / 2;

    const centerY = height / 2;

    // Botones del menú de pausa con el mismo formato que el login (ver uiButton.ts): Opciones y
    // Salir en dorado (primario), Volver en ghost (secundario oscuro). Apilados y centrados; el
    // reposicionado por resize vive en LayoutController.layoutUI (mismos offsets).
    const MENU_BTN_W = 220;
    const MENU_BTN_H = 34;

    const opciones = createUiButton(this.scene, {
      label: 'Opciones',
      palette: UI_BUTTON_PALETTES.gold,
      width: MENU_BTN_W,
      height: MENU_BTN_H,
      fontSize: 18,
      depth: 1100,
      onClick: () => this.openOptionsPanel(),
    });
    opciones.setPosition(centerX, centerY - 44).setVisible(false);

    const salir = createUiButton(this.scene, {
      label: 'Salir',
      palette: UI_BUTTON_PALETTES.gold,
      width: MENU_BTN_W,
      height: MENU_BTN_H,
      fontSize: 18,
      depth: 1100,
      onClick: () => void this.scene.handleLogoutAndReturnToCharacterSelect(),
    });
    salir.setPosition(centerX, centerY).setVisible(false);

    const volver = createUiButton(this.scene, {
      label: 'Volver',
      palette: UI_BUTTON_PALETTES.ghost,
      width: MENU_BTN_W,
      height: MENU_BTN_H,
      fontSize: 16,
      depth: 1100,
      onClick: () => this.closeMainMenu(),
    });
    volver.setPosition(centerX, centerY + 44).setVisible(false);

    this.scene.menuButtons = { opciones, salir, volver };

    // SFX de UI (hover/click) en la tuerca y los botones del menú.
    attachUiSound(this.scene.gearButton);
    attachUiSound(opciones.bg);
    attachUiSound(salir.bg);
    attachUiSound(volver.bg);

    // Atajo de teclado ESC para abrir/cerrar menú (si no estamos escribiendo en el chat)

    this.scene.input.keyboard?.on('keydown-ESC', () => {
      if (this.scene.chatIsFocused) {
        // Si el chat está activo, escapamos del chat primero

        this.scene.chatIsFocused = false;

        this.scene.chatCurrentInput = '';

        this.scene.chat.updateChatInputText();

        return;
      }

      // Si el world map overlay está abierto, ESC lo cierra primero (antes del menú).
      if (this.scene.mapOverlay?.isVisible()) {
        this.scene.mapOverlay.hide();
        return;
      }

      if (this.scene.isMenuOpen) {
        this.closeMainMenu();
      } else {
        this.openMainMenu();
      }
    });
  }

  public openMainMenu(): void {
    this.scene.isMenuOpen = true;

    // El overlay solo oscurece el fondo, pero NO debe bloquear los clics de

    // los botones del menú. Lo dejamos NO interactivo.

    this.scene.menuOverlay?.setVisible(true).setActive(true).removeInteractive();

    if (this.scene.menuButtons) {
      this.scene.menuButtons.opciones.setVisible(true).setActive(true);

      this.scene.menuButtons.salir.setVisible(true).setActive(true);

      this.scene.menuButtons.volver.setVisible(true).setActive(true);
    }

    // Ocultar panel de opciones si estaba abierto

    if (this.scene.optionsPanel) {
      const panel = this.scene.optionsPanel;

      panel.bg.setVisible(false).setActive(false);

      panel.aureumLabel.setVisible(false).setActive(false);

      panel.sectionButtons.forEach((b) => b.setVisible(false).setActive(false));

      panel.contentText.setVisible(false).setActive(false);

      panel.videoToggleGrid.setVisible(false).setActive(false);

      panel.videoToggleCoords.setVisible(false).setActive(false);

      panel.currentTileToggle.setVisible(false).setActive(false);

      panel.closeButton.setVisible(false).setActive(false);

      panel.npcNamesToggle.setVisible(false).setActive(false);

      panel.hostileNpcNamesToggle.setVisible(false).setActive(false);

      panel.borderBlocksToggle.setVisible(false).setActive(false);

      panel.floorBlocksToggle.setVisible(false).setActive(false);

      panel.hudScaleLabel.setVisible(false).setActive(false);

      panel.sliderBg.setVisible(false).setActive(false);

      panel.sliderMarks.forEach((m) => m.setVisible(false).setActive(false));

      panel.sliderThumb.setVisible(false).setActive(false);

      panel.soundControls.setVisible(false);
    }
  }

  public closeMainMenu(): void {
    this.scene.isMenuOpen = false;

    this.scene.menuOverlay?.setVisible(false).setActive(false).removeInteractive();

    if (this.scene.menuButtons) {
      this.scene.menuButtons.opciones.setVisible(false).setActive(false);

      this.scene.menuButtons.salir.setVisible(false).setActive(false);

      this.scene.menuButtons.volver.setVisible(false).setActive(false);
    }

    // También ocultar panel de opciones por si acaso

    if (this.scene.optionsPanel) {
      const panel = this.scene.optionsPanel;

      panel.bg.setVisible(false).setActive(false);

      panel.aureumLabel.setVisible(false).setActive(false);

      panel.sectionButtons.forEach((b) => b.setVisible(false).setActive(false));

      panel.contentText.setVisible(false).setActive(false);

      panel.videoToggleGrid.setVisible(false).setActive(false);

      panel.videoToggleCoords.setVisible(false).setActive(false);

      panel.currentTileToggle.setVisible(false).setActive(false);

      panel.closeButton.setVisible(false).setActive(false);

      panel.npcNamesToggle.setVisible(false).setActive(false);

      panel.hostileNpcNamesToggle.setVisible(false).setActive(false);

      panel.borderBlocksToggle.setVisible(false).setActive(false);

      panel.floorBlocksToggle.setVisible(false).setActive(false);

      panel.hudScaleLabel.setVisible(false).setActive(false);

      panel.sliderBg.setVisible(false).setActive(false);

      panel.sliderMarks.forEach((m) => m.setVisible(false).setActive(false));

      panel.sliderThumb.setVisible(false).setActive(false);

      panel.soundControls.setVisible(false);
    }
  }

  public openOptionsPanel(): void {
    if (!this.scene.menuOverlay) return;

    if (!this.scene.optionsPanel) {
      this.createOptionsPanel();
    }

    // Mientras el panel de opciones está abierto, eliminamos completamente la

    // interacción del overlay para que no bloquee los clics sobre los botones

    // y toggles del panel.

    // NOTE: `removeInteractive` los saca de la lista de objetos interactivos,

    // a diferencia de `disableInteractive` que puede seguir ocupando el hit-test.

    this.scene.menuOverlay.removeInteractive();

    // Ocultar botones del menú principal, mostrar panel de opciones

    if (this.scene.menuButtons) {
      this.scene.menuButtons.opciones.setVisible(false).setActive(false);

      this.scene.menuButtons.salir.setVisible(false).setActive(false);

      this.scene.menuButtons.volver.setVisible(false).setActive(false);
    }

    if (this.scene.optionsPanel) {
      const panel = this.scene.optionsPanel;

      panel.bg.setVisible(true).setActive(true);

      panel.aureumLabel.setVisible(true).setActive(true);

      panel.sectionButtons.forEach((b) => b.setVisible(true).setActive(true));

      panel.contentText.setVisible(true).setActive(true);

      panel.videoToggleGrid.setVisible(panel.videoToggleGrid.visible).setActive(true);

      panel.videoToggleCoords.setVisible(panel.videoToggleCoords.visible).setActive(true);

      panel.currentTileToggle.setVisible(panel.currentTileToggle.visible).setActive(true);

      panel.closeButton.setVisible(true).setActive(true);
    }
  }

  public createOptionsPanel(): void {
    const { width, height } = this.scene.scale;

    const panelWidth = width * 0.6;

    const panelHeight = height * 0.6;

    const panelX = width / 2;

    const panelY = height / 2;

    const bg = this.scene.add

      .rectangle(panelX, panelY, panelWidth, panelHeight, 0x222222, 0.95)

      .setOrigin(0.5, 0.5)

      .setDepth(1100)

      .setScrollFactor(0);

    // Label con la URL del sitio en la parte superior centrado

    const aureumLabel = this.scene.addText(width / 2, 20, SITE_URL, {
        fontSize: '14px',

        color: '#ffffff',
      })

      .setOrigin(0.5, 0)

      .setDepth(1101)

      .setScrollFactor(0);

    // Coordenadas base relativas a la esquina superior izquierda del panel

    const baseX = panelX - panelWidth / 2;

    const baseY = panelY - panelHeight / 2;

    const sections = ['General', 'Video', 'Sonido', 'Teclas', 'Interfaz'];

    const sectionButtons: Phaser.GameObjects.Text[] = [];

    const leftX = baseX + 20;

    let currentY = baseY + 40;

    const contentText = this.scene.addText(baseX + 160, baseY + 40, 'Opciones - General\n\n(placeholder)', {
        fontSize: '14px',

        color: '#ffffff',

        wordWrap: { width: panelWidth - 180 },

        align: 'left',
      })

      .setOrigin(0, 0)

      .setDepth(1100)

      .setScrollFactor(0)


    sections.forEach((section, index) => {
      const btn = this.scene.addText(leftX, currentY, section, {
          fontSize: '14px',

          color: index === 0 ? '#ffffff' : '#aaaaaa',
        })

        .setOrigin(0, 0.5)

        .setInteractive({ useHandCursor: true })

        .setDepth(1100)

        .setScrollFactor(0);

      btn.on('pointerup', () => {
        sectionButtons.forEach((b) => b.setColor('#aaaaaa'));

        btn.setColor('#ffffff');

        if (section === 'Teclas') {
          contentText.setText('Opciones - Teclas\n\nConfigura las teclas para cada acción.\nCada acción puede tener dos teclas asignadas.');
          videoToggleGrid.setVisible(false);
          videoToggleCoords.setVisible(false);
          currentTileToggle.setVisible(false);
          headingArrowToggle.setVisible(false);
          npcNamesToggle.setVisible(false);
          hostileNpcNamesToggle.setVisible(false);
          borderBlocksToggle.setVisible(false);
          floorBlocksToggle.setVisible(false);
          hudScaleLabel.setVisible(false);
          sliderBg.setVisible(false);
          sliderMarks.forEach(m => m.setVisible(false));
          sliderThumb.setVisible(false);
          soundControls.setVisible(false);
          showKeyBindingRows(true);
          refreshKeyBindingUI();
        } else if (section === 'Video') {
          contentText.setText(
            'Opciones - Video\n\n' +
            'Configura elementos de depuración visual del mapa.\n' +
            'Estas opciones no afectan al servidor, sólo a tu cliente.'
          );
          videoToggleGrid.setVisible(true);
          videoToggleCoords.setVisible(true);
          currentTileToggle.setVisible(true);
          headingArrowToggle.setVisible(false);
          npcNamesToggle.setVisible(true);
          hostileNpcNamesToggle.setVisible(true);
          borderBlocksToggle.setVisible(true);
          floorBlocksToggle.setVisible(true);
          hudScaleLabel.setVisible(true);
          sliderBg.setVisible(true);
          sliderMarks.forEach(m => m.setVisible(true));
          sliderThumb.setVisible(true);
          soundControls.setVisible(false);
          showKeyBindingRows(false);
          // Actualizar posición del thumb según escala actual
          const currentIdx = hudScaleValues.indexOf(this.scene.hud.hudScale);
          const idx = currentIdx >= 0 ? currentIdx : 1;
          sliderThumb.setX(sliderX + (idx / (hudScaleValues.length - 1)) * sliderWidth);
          refreshVideoOptions();
          refreshNpcNameVisibilityOption();
          refreshBlockDebugOptions();
        } else if (section === 'Interfaz') {
          contentText.setText(
            'Opciones - Interfaz\n\n' +
            'Configura elementos de la interfaz del juego.'
          );
          videoToggleGrid.setVisible(false);
          videoToggleCoords.setVisible(false);
          currentTileToggle.setVisible(false);
          npcNamesToggle.setVisible(false);
          hostileNpcNamesToggle.setVisible(false);
          borderBlocksToggle.setVisible(false);
          floorBlocksToggle.setVisible(false);
          hudScaleLabel.setVisible(false);
          sliderBg.setVisible(false);
          sliderMarks.forEach(m => m.setVisible(false));
          sliderThumb.setVisible(false);
          soundControls.setVisible(false);
          showKeyBindingRows(false);
          headingArrowToggle.setVisible(true);
          refreshInterfaceOptions();
        } else if (section === 'Sonido') {
          contentText.setText(
            'Opciones - Sonido\n\n' +
            'Volumen maestro y por canal. Cada canal se puede silenciar por separado.\n' +
            'Estas opciones no afectan al servidor, sólo a tu cliente.'
          );
          videoToggleGrid.setVisible(false);
          videoToggleCoords.setVisible(false);
          currentTileToggle.setVisible(false);
          headingArrowToggle.setVisible(false);
          npcNamesToggle.setVisible(false);
          hostileNpcNamesToggle.setVisible(false);
          borderBlocksToggle.setVisible(false);
          floorBlocksToggle.setVisible(false);
          hudScaleLabel.setVisible(false);
          sliderBg.setVisible(false);
          sliderMarks.forEach(m => m.setVisible(false));
          sliderThumb.setVisible(false);
          showKeyBindingRows(false);
          soundControls.setVisible(true);
        } else {
          contentText.setText(`Opciones - ${section}\n\n(placeholder)`);
          videoToggleGrid.setVisible(false);
          videoToggleCoords.setVisible(false);
          currentTileToggle.setVisible(false);
          headingArrowToggle.setVisible(false);
          npcNamesToggle.setVisible(false);
          hostileNpcNamesToggle.setVisible(false);
          borderBlocksToggle.setVisible(false);
          floorBlocksToggle.setVisible(false);
          hudScaleLabel.setVisible(false);
          sliderBg.setVisible(false);
          sliderMarks.forEach(m => m.setVisible(false));
          sliderThumb.setVisible(false);
          soundControls.setVisible(false);
          showKeyBindingRows(false);
        }
      });

      sectionButtons.push(btn);

      currentY += 24;
    });

    // Controles específicos de la sección "Video"

    // Un poco más abajo para que no se superpongan con el texto de contenido

    const videoOptionYStart = baseY + 120;

    // 1) Opción de visibilidad de nombres de NPC neutral (primera en la lista)

    const npcNamesToggle = this.scene.addText(baseX + 160, videoOptionYStart, '', {
        fontSize: '14px',

        color: '#ffffff',
      })

      .setOrigin(0, 0)

      .setInteractive({ useHandCursor: true })

      .setDepth(1100)

      .setScrollFactor(0)


    // 1b) Opción de visibilidad de nombres de NPC hostil

    const hostileNpcNamesToggle = this.scene.addText(baseX + 160, videoOptionYStart + 24, '', {
        fontSize: '14px',

        color: '#ffffff',
      })

      .setOrigin(0, 0)

      .setInteractive({ useHandCursor: true })

      .setDepth(1100)

      .setScrollFactor(0)


    // 2) Mostrar grilla

    const videoToggleGrid = this.scene.addText(baseX + 160, videoOptionYStart + 48, '[ ] Mostrar grilla del mapa', {
        fontSize: '14px',

        color: '#ffffff',
      })

      .setOrigin(0, 0)

      .setInteractive({ useHandCursor: true })

      .setDepth(1100)

      .setScrollFactor(0)


    // 3) Mostrar coordenadas

    const videoToggleCoords = this.scene.addText(baseX + 160, videoOptionYStart + 72, '[ ] Mostrar coordenadas (x,y) de tiles', {
        fontSize: '14px',

        color: '#ffffff',
      })

      .setOrigin(0, 0)

      .setInteractive({ useHandCursor: true })

      .setDepth(1100)

      .setScrollFactor(0)


    // 4) Bloqueos de borde

    const borderBlocksToggle = this.scene.addText(baseX + 160, videoOptionYStart + 96, '', {
        fontSize: '14px',

        color: '#ffffff',
      })

      .setOrigin(0, 0)

      .setInteractive({ useHandCursor: true })

      .setDepth(1100)

      .setScrollFactor(0)


    // 5) Bloqueos de piso

    const floorBlocksToggle = this.scene.addText(baseX + 160, videoOptionYStart + 120, '', {
        fontSize: '14px',

        color: '#ffffff',
      })

      .setOrigin(0, 0)

      .setInteractive({ useHandCursor: true })

      .setDepth(1100)

      .setScrollFactor(0)


    // 6) Tile actual del jugador

    const currentTileToggle = this.scene.addText(baseX + 160, videoOptionYStart + 144, '', {
        fontSize: '14px',

        color: '#ffffff',
      })

      .setOrigin(0, 0)

      .setInteractive({ useHandCursor: true })

      .setDepth(1100)

      .setScrollFactor(0)


    // 7) Slider de tamaño del HUD

    const hudScaleLabel = this.scene.addText(baseX + 160, videoOptionYStart + 174, 'Tamaño del HUD:', {
        fontSize: '14px',

        color: '#ffffff',
      })

      .setOrigin(0, 0)

      .setDepth(1100)

      .setScrollFactor(0)


    const hudScaleValues = GameScene.HUD_SCALE_VALUES;

    const hudScaleLabels = GameScene.HUD_SCALE_LABELS;

    const sliderWidth = 120;

    const sliderX = baseX + 160;

    const sliderY = videoOptionYStart + 199;

    // Fondo del slider

    const sliderBg = this.scene.add

      .rectangle(sliderX + sliderWidth / 2, sliderY, sliderWidth, 8, 0x444444)

      .setOrigin(0.5, 0.5)

      .setDepth(1100)

      .setScrollFactor(0);

    // Marcas del slider

    const sliderMarks: Phaser.GameObjects.Text[] = [];

    for (let i = 0; i < hudScaleValues.length; i++) {
      const markX = sliderX + (i / (hudScaleValues.length - 1)) * sliderWidth;

      const mark = this.scene.add

        .text(markX, sliderY + 12, hudScaleLabels[i], {
          fontSize: '10px',

          color: '#aaaaaa',
        })

        .setOrigin(0.5, 0)

        .setDepth(1100)

        .setScrollFactor(0);

      sliderMarks.push(mark);
    }

    // Botón del slider (thumb)

    const getSliderIndex = (): number => {
      const idx = hudScaleValues.indexOf(this.scene.hud.hudScale);

      return idx >= 0 ? idx : 1; // Default a x1 si no se encuentra
    };

    const sliderThumb = this.scene.add

      .circle(sliderX + (getSliderIndex() / (hudScaleValues.length - 1)) * sliderWidth, sliderY, 8, 0xffffff)

      .setDepth(1101)

      .setScrollFactor(0)

      .setInteractive({ useHandCursor: true, draggable: true });

    // Hacer clickeable el fondo del slider

    sliderBg.setInteractive({ useHandCursor: true });

    sliderBg.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      const localX = pointer.x - sliderX;

      const ratio = Phaser.Math.Clamp(localX / sliderWidth, 0, 1);

      const closestIndex = Math.round(ratio * (hudScaleValues.length - 1));

      this.scene.hud.setHUDScale(hudScaleValues[closestIndex]);

      sliderThumb.setX(sliderX + (closestIndex / (hudScaleValues.length - 1)) * sliderWidth);
    });

    // Drag del thumb

    this.scene.input.setDraggable(sliderThumb);

    sliderThumb.on('drag', (_pointer: Phaser.Input.Pointer, dragX: number) => {
      const clampedX = Phaser.Math.Clamp(dragX, sliderX, sliderX + sliderWidth);

      const ratio = (clampedX - sliderX) / sliderWidth;

      const closestIndex = Math.round(ratio * (hudScaleValues.length - 1));

      sliderThumb.setX(sliderX + (closestIndex / (hudScaleValues.length - 1)) * sliderWidth);

      this.scene.hud.setHUDScale(hudScaleValues[closestIndex]);
    });

    // Inicialmente ocultos

    hudScaleLabel.setVisible(false);

    sliderBg.setVisible(false);

    sliderMarks.forEach(m => m.setVisible(false));

    sliderThumb.setVisible(false);

    // Base Y para los controles de la sección Interfaz (las fuentes ya no son
    // configurables; queda la flecha de dirección).
    const fontOptionYStart = baseY + 120;

    // Flecha de heading (en sección Interfaz)
    const headingArrowToggle = this.scene.addText(baseX + 160, fontOptionYStart + 200, '', {
        fontSize: '14px',
        color: '#ffffff',
      })
      .setOrigin(0, 0)
      .setInteractive({ useHandCursor: true })
      .setDepth(1100)
      .setScrollFactor(0)
      .setVisible(false);

    const refreshVideoOptions = (): void => {
      videoToggleGrid.setText(`[${this.scene.showGrid ? 'X' : ' '}] Mostrar grilla del mapa`);

      videoToggleCoords.setText(

        `[${this.scene.showGridCoords ? 'X' : ' '}] Mostrar coordenadas (x,y) de tiles`

      );

      currentTileToggle.setText(

        `[${this.scene.showPlayerTile ? 'X' : ' '}] Mostrar tile actual de jugador`

      );

      // Recalcular el área interactiva después de cambiar el texto para que

      // el hit-area coincida con el tamaño actual.

      videoToggleGrid.setInteractive({ useHandCursor: true });

      videoToggleCoords.setInteractive({ useHandCursor: true });

      currentTileToggle.setInteractive({ useHandCursor: true });
    };

    const refreshNpcNameVisibilityOption = (): void => {
      const modeLabel =

        this.scene.npcNameVisibilityMode === 'always' ? 'Siempre visible' : 'Al pasar el mouse';

      npcNamesToggle.setText(`Visibilidad nombres de NPC: ${modeLabel}`);

      const hostileModeLabel =
        this.scene.hostileNpcNameVisibilityMode === 'always' ? 'Siempre visible' : 'Al pasar el mouse';

      hostileNpcNamesToggle.setText(`Visibilidad nombres de NPC hostil: ${hostileModeLabel}`);
    };

    const refreshBlockDebugOptions = (): void => {
      borderBlocksToggle.setText(

        `[${this.scene.showBorderBlocks ? 'X' : ' '}] Mostrar bloqueos borde de mapa`

      );

      floorBlocksToggle.setText(

        `[${this.scene.showFloorBlocks ? 'X' : ' '}] Mostrar bloqueos de piso`

      );

      borderBlocksToggle.setInteractive({ useHandCursor: true });

      floorBlocksToggle.setInteractive({ useHandCursor: true });
    };

    const refreshInterfaceOptions = (): void => {
      // Toggle de flecha de dirección (única opción de la sección Interfaz).
      headingArrowToggle.setText(
        `[${this.scene.showHeadingArrow ? 'X' : ' '}] Mostrar flecha de dirección`
      );
      headingArrowToggle.setInteractive({ useHandCursor: true });
    };

    videoToggleGrid.on('pointerup', () => {
      this.scene.showGrid = !this.scene.showGrid;

      refreshVideoOptions();

      this.scene.drawGrid();

      this.scene.persistence.saveClientOptions();
    });

    videoToggleCoords.on('pointerup', () => {
      this.scene.showGridCoords = !this.scene.showGridCoords;

      refreshVideoOptions();

      this.scene.drawGrid();

      this.scene.persistence.saveClientOptions();
    });

    npcNamesToggle.on('pointerup', () => {
      this.scene.npcNameVisibilityMode =

        this.scene.npcNameVisibilityMode === 'always' ? 'hover' : 'always';

      refreshNpcNameVisibilityOption();

      // Aplicar YA: el loop per-frame (update) está pausado mientras el menú/opciones
      // está abierto (isMenuOpen), así que el cambio no se reflejaría hasta cerrar.
      this.scene.targets.updateNpcNameVisibility();

      this.scene.persistence.saveClientOptions();
    });

    hostileNpcNamesToggle.on('pointerup', () => {
      this.scene.hostileNpcNameVisibilityMode =
        this.scene.hostileNpcNameVisibilityMode === 'always' ? 'hover' : 'always';

      refreshNpcNameVisibilityOption();

      // Aplicar YA (ver nota en el toggle de NPC neutral: update está pausado con el menú).
      this.scene.targets.updateNpcNameVisibility();

      this.scene.persistence.saveClientOptions();
    });

    borderBlocksToggle.on('pointerup', () => {
      this.scene.showBorderBlocks = !this.scene.showBorderBlocks;

      refreshBlockDebugOptions();

      this.scene.drawGrid();

      this.scene.persistence.saveClientOptions();
    });

    floorBlocksToggle.on('pointerup', () => {
      this.scene.showFloorBlocks = !this.scene.showFloorBlocks;

      refreshBlockDebugOptions();

      this.scene.drawGrid();

      this.scene.persistence.saveClientOptions();
    });

    currentTileToggle.on('pointerup', () => {
      this.scene.showPlayerTile = !this.scene.showPlayerTile;

      refreshVideoOptions();

      this.scene.tileHighlight.refreshPlayerTileHighlight();

      this.scene.persistence.saveClientOptions();
    });

    headingArrowToggle.on('pointerup', () => {
      this.scene.showHeadingArrow = !this.scene.showHeadingArrow;

      refreshInterfaceOptions();

      this.scene.npcSync.applyHeadingArrowVisibility();

      this.scene.persistence.saveClientOptions();
    });

    // =====================================================
    // Controles específicos de la sección "Teclas"
    // =====================================================
    const keysOptionYStart = baseY + 120;
    const keyBindingRows: {
      label: Phaser.GameObjects.Text;
      primary: Phaser.GameObjects.Text | null;
      secondary: Phaser.GameObjects.Text | null;
      action: ActionKeyId | null;
      titleText?: Phaser.GameObjects.Text;
    }[] = [];

    const actionLabels: Record<ActionKeyId, string> = {
      moveUp: 'Mover arriba',
      moveDown: 'Mover abajo',
      moveLeft: 'Mover izquierda',
      moveRight: 'Mover derecha',
      attack: 'Ataque básico',
      inventory: 'Mostrar/ocultar Inventario',
      pickup: 'Recoger Item',
      toggleMenu: 'Mostrar/ocultar Menu de pausa',
      equip: 'Equipar Item',
      characterInfo: 'Mostrar/ocultar Información PJ',
      dropItem: 'Tirar Item',
      passiveTree: 'Mostrar/ocultar Espíritu',
      spells: 'Mostrar/ocultar Hechizos',
      skills: 'Mostrar/ocultar Habilidades',
      spell1: 'Hechizo slot 1',
      spell2: 'Hechizo slot 2',
      spell3: 'Hechizo slot 3',
      spell4: 'Hechizo slot 4',
      spell5: 'Hechizo slot 5',
      spell6: 'Hechizo slot 6',
      spell7: 'Hechizo slot 7',
      spell8: 'Hechizo slot 8',
      spell9: 'Hechizo slot 9',
      spell10: 'Hechizo slot 10',
      meditate: 'Meditar',
      clearTarget: 'Deseleccionar objetivo',
      cycleTarget: 'Targetear enemigo (Tab / Shift+Tab inverso)',
      toggleMap: 'Abrir/cerrar Mapa',
      toggleBoat: 'Embarcar / Desembarcar',
      cycleSummonMode: 'Modo de invocaciones (defensivo/pasivo/agresivo)',
      useItem: 'Usar item seleccionado (pociones/amuletos usables)',
    };

    // Orden y agrupación de acciones con títulos
    type KeyBindingRow = 
      | { type: 'title'; text: string }
      | { type: 'action'; action: ActionKeyId };
    
    const keyBindingOrder: KeyBindingRow[] = [
      { type: 'title', text: 'Movimiento' },
      { type: 'action', action: 'moveUp' },
      { type: 'action', action: 'moveDown' },
      { type: 'action', action: 'moveLeft' },
      { type: 'action', action: 'moveRight' },
      { type: 'title', text: 'Interacción' },
      { type: 'action', action: 'attack' },
      { type: 'action', action: 'pickup' },
      { type: 'action', action: 'dropItem' },
      { type: 'action', action: 'equip' },
      { type: 'action', action: 'useItem' },
      { type: 'action', action: 'toggleBoat' },
      { type: 'title', text: 'Hechizos' },
      { type: 'action', action: 'spells' },
      { type: 'action', action: 'spell1' },
      { type: 'action', action: 'spell2' },
      { type: 'action', action: 'spell3' },
      { type: 'action', action: 'spell4' },
      { type: 'action', action: 'spell5' },
      { type: 'action', action: 'spell6' },
      { type: 'action', action: 'spell7' },
      { type: 'action', action: 'spell8' },
      { type: 'action', action: 'spell9' },
      { type: 'action', action: 'spell10' },
      { type: 'action', action: 'meditate' },
      { type: 'action', action: 'clearTarget' },
      { type: 'action', action: 'cycleTarget' },
      { type: 'action', action: 'cycleSummonMode' },
      { type: 'title', text: 'Ventanas' },
      { type: 'action', action: 'inventory' },
      { type: 'action', action: 'characterInfo' },
      { type: 'action', action: 'passiveTree' },
      { type: 'action', action: 'toggleMap' },
      { type: 'action', action: 'toggleMenu' },
    ];

    // Estado para captura de tecla
    let waitingForKey: { action: ActionKeyId; slot: 'primary' | 'secondary'; button: Phaser.GameObjects.Text } | null = null;

    const startKeyCapture = (action: ActionKeyId, slot: 'primary' | 'secondary', button: Phaser.GameObjects.Text): void => {
      // Si ya estamos esperando en otro botón, restaurarlo
      if (waitingForKey) {
        const existingBinding = this.scene.inputManager?.getBinding(waitingForKey.action);
        const oldKey = slot === 'primary'
          ? existingBinding?.primary
          : existingBinding?.secondary;
        waitingForKey.button.setText(this.scene.getKeyName(oldKey));
      }
      
      waitingForKey = { action, slot, button };
      button.setText('...');
      button.setBackgroundColor('#666666');
    };

    const cancelKeyCapture = (): void => {
      if (waitingForKey) {
        const binding = this.scene.inputManager?.getBinding(waitingForKey.action);
        const key = waitingForKey.slot === 'primary'
          ? binding?.primary
          : binding?.secondary;
        waitingForKey.button.setText(this.scene.getKeyName(key));
        waitingForKey.button.setBackgroundColor(waitingForKey.slot === 'primary' ? '#444444' : '#333333');
        waitingForKey = null;
      }
    };

    const isKeyAlreadyUsed = (keyCode: number, excludeAction: ActionKeyId, excludeSlot: 'primary' | 'secondary'): boolean => {
      for (const act of ACTION_KEY_IDS) {
        const binding = this.scene.inputManager?.getBinding(act);
        // Verificar tecla primaria
        if (binding?.primary === keyCode) {
          if (act !== excludeAction || excludeSlot !== 'primary') {
            return true;
          }
        }
        // Verificar tecla secundaria
        if (binding?.secondary === keyCode) {
          if (act !== excludeAction || excludeSlot !== 'secondary') {
            return true;
          }
        }
      }
      return false;
    };

    const handleKeyCaptured = (keyCode: number): void => {
      if (!waitingForKey) return;
      
      const { action, slot, button } = waitingForKey;
      
      // Verificar si la tecla ya está ocupada por otra acción
      if (isKeyAlreadyUsed(keyCode, action, slot)) {
        // Mostrar en rojo y continuar escuchando
        button.setText(this.scene.getKeyName(keyCode));
        button.setBackgroundColor('#882222');
        
        // Después de 300ms, volver a mostrar "..."
        this.scene.time.delayedCall(300, () => {
          if (waitingForKey && waitingForKey.button === button) {
            button.setText('...');
            button.setBackgroundColor('#666666');
          }
        });
        return; // No asignar, seguir escuchando
      }
      
      // Asignar la tecla
      this.scene.inputManager?.setBinding(action, slot, keyCode);
      
      // Actualizar UI
      button.setText(this.scene.getKeyName(keyCode));
      button.setBackgroundColor(slot === 'primary' ? '#444444' : '#333333');
      
      // Guardar configuración
      this.scene.persistence.saveClientOptions();
      
      // Reconfigurar las teclas de Phaser
      this.scene.configureActionKeys();
      
      waitingForKey = null;
    };

    // Listener global para captura de tecla
    const keyCaptureHandler = (event: KeyboardEvent): void => {
      if (!waitingForKey) return;
      
      event.preventDefault();
      event.stopPropagation();
      
      // ESC cancela la captura
      if (event.keyCode === Phaser.Input.Keyboard.KeyCodes.ESC) {
        cancelKeyCapture();
        return;
      }
      
      handleKeyCaptured(event.keyCode);
    };

    // Agregar el listener cuando se muestra la sección de teclas
    const enableKeyCapture = (): void => {
      window.addEventListener('keydown', keyCaptureHandler, true);
    };

    const disableKeyCapture = (): void => {
      cancelKeyCapture();
      window.removeEventListener('keydown', keyCaptureHandler, true);
    };

    keyBindingOrder.forEach((row, idx) => {
      const rowY = keysOptionYStart + idx * 24;
      
      if (row.type === 'title') {
        // Título de grupo - sin botones de mapeo
        const titleText = this.scene.addText(baseX + 160, rowY, row.text, {
          fontSize: '13px',
          color: '#ffffff',
          fontStyle: 'bold',
        })
          .setOrigin(0, 0.5)
          .setDepth(1100)
          .setScrollFactor(0)
          .setVisible(false);
        
        keyBindingRows.push({ 
          label: titleText, 
          primary: null, 
          secondary: null, 
          action: null,
          titleText 
        });
      } else {
        // Acción con botones de mapeo
        const action = row.action;
        const binding = this.scene.inputManager?.getBinding(action);
        
        // Label de la acción
        const label = this.scene.addText(baseX + 160, rowY, actionLabels[action] + ':', {
            fontSize: '12px',
            color: '#cccccc',
          })
          .setOrigin(0, 0.5)
          .setDepth(1100)
          .setScrollFactor(0)
          .setVisible(false)

        // Botón tecla primaria
        const primary = this.scene.addText(baseX + 370, rowY, this.scene.getKeyName(binding?.primary), {
            fontSize: '12px',
            color: '#ffffff',
            backgroundColor: '#444444',
            padding: { left: 6, right: 6, top: 3, bottom: 3 },
          })
            .setOrigin(0, 0.5)
            .setInteractive({ useHandCursor: true })
            .setDepth(1100)
            .setScrollFactor(0)
            .setVisible(false)

        primary.on('pointerup', () => {
          startKeyCapture(action, 'primary', primary);
        });

        // Botón tecla secundaria
        const secondary = this.scene.addText(baseX + 460, rowY, this.scene.getKeyName(binding?.secondary), {
            fontSize: '12px',
            color: '#aaaaaa',
            backgroundColor: '#333333',
            padding: { left: 6, right: 6, top: 3, bottom: 3 },
          })
          .setOrigin(0, 0.5)
          .setInteractive({ useHandCursor: true })
          .setDepth(1100)
          .setScrollFactor(0)
          .setVisible(false)

        secondary.on('pointerup', () => {
          startKeyCapture(action, 'secondary', secondary);
        });

        keyBindingRows.push({ label, primary, secondary, action });
      }
    });

    const refreshKeyBindingUI = (): void => {
      keyBindingRows.forEach((row) => {
        if (row.action && row.primary && row.secondary) {
          const rowBinding = this.scene.inputManager?.getBinding(row.action);
          row.primary.setText(this.scene.getKeyName(rowBinding?.primary));
          row.secondary.setText(this.scene.getKeyName(rowBinding?.secondary));
        }
      });
    };

    const showKeyBindingRows = (visible: boolean): void => {
      keyBindingRows.forEach((row) => {
        row.label.setVisible(visible);
        if (row.primary) row.primary.setVisible(visible);
        if (row.secondary) row.secondary.setVisible(visible);
      });
      if (visible) {
        enableKeyCapture();
      } else {
        disableKeyCapture();
      }
    };

    // Inicialmente los toggles solo se muestran en la sección Video
    videoToggleGrid.setVisible(false);
    videoToggleCoords.setVisible(false);
    npcNamesToggle.setVisible(false);
    hostileNpcNamesToggle.setVisible(false);
    borderBlocksToggle.setVisible(false);
    floorBlocksToggle.setVisible(false);
    currentTileToggle.setVisible(false);
    showKeyBindingRows(false);

    const closeButton = this.scene.addText(baseX + panelWidth - 16, baseY + 16, '✕', {
        fontSize: '16px',

        color: '#ffffff',

        backgroundColor: '#555555',

        padding: { left: 6, right: 6, top: 2, bottom: 2 },
      })

      .setOrigin(1, 0)

      .setInteractive({ useHandCursor: true })

      .setDepth(1100)

      .setScrollFactor(0);

    closeButton.on('pointerup', () => {
      // Cerrar panel de opciones y volver al menú principal

      if (this.scene.optionsPanel) {
        const panel = this.scene.optionsPanel;

        panel.bg.setVisible(false).setActive(false);

        panel.aureumLabel.setVisible(false).setActive(false);

        panel.sectionButtons.forEach((b) => b.setVisible(false).setActive(false));

        panel.contentText.setVisible(false).setActive(false);

        panel.videoToggleGrid.setVisible(false).setActive(false);

        panel.videoToggleCoords.setVisible(false).setActive(false);

        panel.currentTileToggle.setVisible(false).setActive(false);

        npcNamesToggle.setVisible(false).setActive(false);

        hostileNpcNamesToggle.setVisible(false).setActive(false);

        panel.closeButton.setVisible(false).setActive(false);

        borderBlocksToggle.setVisible(false).setActive(false);

        floorBlocksToggle.setVisible(false).setActive(false);

        hudScaleLabel.setVisible(false).setActive(false);

        sliderBg.setVisible(false).setActive(false);

        sliderMarks.forEach(m => m.setVisible(false).setActive(false));
        sliderThumb.setVisible(false).setActive(false);

        panel.headingArrowToggle.setVisible(false).setActive(false);
        panel.soundControls.setVisible(false);
        showKeyBindingRows(false);
      }
      if (this.scene.menuButtons) {
        this.scene.menuButtons.opciones.setVisible(true).setActive(true);

        this.scene.menuButtons.salir.setVisible(true).setActive(true);

        this.scene.menuButtons.volver.setVisible(true).setActive(true);
      }
    });

    // Controles de la sección "Sonido" (creados dinámicamente, arrancan ocultos).
    const soundControls = this.createSoundControls(baseX, baseY, panelWidth);

    // Inicialmente todo oculto

    bg.setVisible(false).setActive(false);

    aureumLabel.setVisible(false).setActive(false);

    sectionButtons.forEach((b) => b.setVisible(false).setActive(false));

    contentText.setVisible(false).setActive(false);

    videoToggleGrid.setVisible(false).setActive(false);

    videoToggleCoords.setVisible(false).setActive(false);

    npcNamesToggle.setVisible(false).setActive(false);

    hostileNpcNamesToggle.setVisible(false).setActive(false);

    closeButton.setVisible(false).setActive(false);

    this.scene.optionsPanel = {
      bg,

      aureumLabel,

      sectionButtons,

      contentText,

      videoToggleGrid,

      videoToggleCoords,

      currentTileToggle,

      headingArrowToggle,

      closeButton,

      npcNamesToggle,

      hostileNpcNamesToggle,

      borderBlocksToggle,

      floorBlocksToggle,

      hudScaleLabel,

      sliderBg,

      sliderMarks,

      sliderThumb,

      soundControls,
    };
  }

  /**
   * Construye los controles de la sección "Sonido": slider de volumen maestro, y por cada categoría
   * (Música/Entorno/Pasos/Combate/Hechizos/NPCs/Interfaz/Latido) una fila con toggle [X] + slider de
   * volumen; más un toggle de "acelerar música con vida baja". Cambios se aplican EN VIVO al
   * AudioManager y se persisten. Arrancan ocultos (sólo se muestran en la sección Sonido).
   * Ver docs/SOUND_AND_MUSIC.md.
   */
  public createSoundControls(baseX: number, baseY: number, panelWidth: number): SoundControls {
    const scene = this.scene;
    const audio = scene.audio;
    const colX = baseX + 160;
    const startY = baseY + 128;
    const rowH = 26;
    const sliderW = Math.min(120, panelWidth - 420);
    const sliderX = colX + 220;
    const depth = 1100;

    const objects: Phaser.GameObjects.GameObject[] = [];
    const refreshers: Array<() => void> = [];

    const persist = (): void => scene.persistence.saveClientOptions();

    const addText = (x: number, y: number, text: string, color = '#ffffff'): Phaser.GameObjects.Text => {
      const t = scene
        .addText(x, y, text, { fontSize: '13px', color })
        .setOrigin(0, 0.5)
        .setDepth(depth)
        .setScrollFactor(0)
        .setVisible(false);
      objects.push(t);
      return t;
    };

    // Slider genérico 0..1: fondo + thumb. Devuelve un refresh que reposiciona el thumb.
    const makeSlider = (
      y: number,
      getValue: () => number,
      setValue: (v: number) => void
    ): (() => void) => {
      const bg = scene.add
        .rectangle(sliderX + sliderW / 2, y, sliderW, 6, 0x444444)
        .setDepth(depth)
        .setScrollFactor(0)
        .setVisible(false)
        .setInteractive({ useHandCursor: true });
      const thumb = scene.add
        .circle(sliderX + getValue() * sliderW, y, 7, 0xffffff)
        .setDepth(depth + 1)
        .setScrollFactor(0)
        .setVisible(false)
        .setInteractive({ useHandCursor: true, draggable: true });
      objects.push(bg, thumb);

      const apply = (clientX: number): void => {
        const ratio = Phaser.Math.Clamp((clientX - sliderX) / sliderW, 0, 1);
        thumb.setX(sliderX + ratio * sliderW);
        setValue(ratio);
        persist();
      };
      bg.on('pointerup', (p: Phaser.Input.Pointer) => apply(p.x));
      scene.input.setDraggable(thumb);
      thumb.on('drag', (_p: Phaser.Input.Pointer, dragX: number) => apply(dragX));

      return (): void => {
        thumb.setX(sliderX + Phaser.Math.Clamp(getValue(), 0, 1) * sliderW);
      };
    };

    let y = startY;

    // --- Volumen maestro ---
    addText(colX, y, 'Volumen maestro');
    refreshers.push(makeSlider(y, () => audio.getConfig().masterVolume, (v) => audio.setMasterVolume(v)));
    y += rowH + 6;

    // --- Por categoría: toggle + slider ---
    for (const cat of SOUND_CATEGORY_ORDER) {
      const label = SOUND_CATEGORY_LABELS_ES[cat];
      const toggle = addText(colX, y, '');
      toggle.setInteractive({ useHandCursor: true });
      const refreshToggle = (): void => {
        const st = audio.getConfig().categories[cat];
        toggle.setText(`[${st?.enabled ? 'X' : ' '}] ${label}`);
        toggle.setInteractive({ useHandCursor: true });
      };
      toggle.on('pointerup', () => {
        const st = audio.getConfig().categories[cat];
        audio.setCategoryEnabled(cat, !st?.enabled);
        refreshToggle();
        persist();
      });
      refreshers.push(refreshToggle);
      refreshers.push(
        makeSlider(
          y,
          () => audio.getConfig().categories[cat]?.volume ?? 1,
          (v) => audio.setCategoryVolume(cat, v)
        )
      );
      y += rowH;
    }

    y += 8;

    // --- Acelerar música con vida baja ---
    const lowHpToggle = addText(colX, y, '');
    lowHpToggle.setInteractive({ useHandCursor: true });
    const refreshLowHp = (): void => {
      lowHpToggle.setText(
        `[${audio.getConfig().musicLowHpSpeedup ? 'X' : ' '}] Acelerar música con vida baja`
      );
      lowHpToggle.setInteractive({ useHandCursor: true });
    };
    lowHpToggle.on('pointerup', () => {
      const cfg = audio.getConfig();
      cfg.musicLowHpSpeedup = !cfg.musicLowHpSpeedup;
      refreshLowHp();
      persist();
    });
    refreshers.push(refreshLowHp);

    const refresh = (): void => refreshers.forEach((r) => r());

    return {
      objects,
      refresh,
      setVisible: (visible: boolean): void => {
        objects.forEach((o) =>
          (o as unknown as Phaser.GameObjects.Components.Visible).setVisible(visible)
        );
        if (visible) refresh();
      },
    };
  }

}
